import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, Link } from 'react-router-dom';
import {
  Box,
  Button,
  TextField,
  Typography,
  InputAdornment,
  IconButton,
  CircularProgress,
  Grid,
  Dialog,
  Snackbar,
  Alert,
  Divider,
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { Google as GoogleIcon } from '@mui/icons-material';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { GoogleOAuthProvider, GoogleLogin } from '@react-oauth/google';

// Redux actions
import { loginDoctor } from '../../../redux/middleware/user/doctor/login';
import { doctorGoogleLogin } from '../../../redux/middleware/user/doctor/googleAuth';
import {
  doctorVerifyEmail,
  doctorResendVerificationEmail,
} from '../../../redux/middleware/user/doctor/verification';
import { doctorRequestPasswordReset } from '../../../redux/middleware/user/doctor/resetPassword';
import {
  openModal,
  closeModal,
} from '../../../redux/middleware/user/doctor/modal';

// Components
import CaptchaSection from './captchaSection';

// Assets
import image1 from './images/image33.avif';
import { shades } from '../../../../theme.js';

// Styled components
const StyledTextField = {
  '& .MuiOutlinedInput-root': {
    '& fieldset': {
      borderColor: shades.neutral[300],
      borderRadius: '8px',
    },
    '&:hover fieldset': {
      borderColor: '#5185ac',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#13334c',
    },
  },
  '& .MuiInputBase-input': {
    color: 'black',
    p: 2,
  },
  '& .MuiInputLabel-root': {
    color: '#13334c',
  },
};

const StyledButton = {
  background: 'linear-gradient(45deg, #13334c 30%, #5185ac 90%)',
  borderRadius: '8px',
  border: 0,
  color: 'white',
  height: 48,
  padding: '0 30px',
  boxShadow: '0 3px 5px 2px rgba(19, 51, 76, .3)',
  transition: 'all 0.3s ease-in-out',
  '&:hover': {
    background: 'linear-gradient(45deg, #0f2b40 30%, #456d8f 90%)',
    transform: 'scale(1.02)',
  },
};

/**
 * DoctorLogin component handles doctor authentication
 * including email/password login, Google login, and password reset
 */
const DoctorLogin = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const recaptchaRef = useRef(null);

  // Redux state
  const { success, isVerified, isModalOpen } = useSelector(
    (state) => state.doctorLogin
  );
  const { verification_id, loading } = useSelector(
    (state) => state.doctorCaptcha
  );

  // Form state
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // UI state
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [showVerificationDialog, setShowVerificationDialog] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info',
  });

  // Captcha state
  const [isCaptchaVerified, setIsCaptchaVerified] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);
  const [recaptchaToken, setRecaptchaToken] = useState(null);
  const [captchaError, setCaptchaError] = useState(null);
  
  // Configuration
  const recaptchaSiteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
  const clientId = process.env.GOOGLE_CLIENT_ID;

  // Logging for debugging in development only
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log(
        '[DEBUG] DoctorLogin Component - RECAPTCHA_SITE_KEY:',
        recaptchaSiteKey
      );
    }
  }, [recaptchaSiteKey]);

  // Handle form field changes
  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  }, []);

  // Handle reCAPTCHA verification
  const handleRecaptchaChange = useCallback((token) => {
    if (process.env.NODE_ENV === 'development') {
      console.log("reCAPTCHA token received");
    }
    
    setRecaptchaToken(token);
    
    if (token) {
      setIsCaptchaVerified(true);
      setSuccessMessage('Captcha verified!');
      setCaptchaError(null);
    } else {
      setIsCaptchaVerified(false);
      setSuccessMessage(null);
    }
  }, []);

  // Form validation
  const validateForm = useCallback(() => {
    const newErrors = {};
    if (!formData.email) newErrors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) 
      newErrors.email = 'Invalid email format';
      
    if (!formData.password) newErrors.password = 'Password is required';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData]);

  // Handle form submission
  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();

    if (!recaptchaToken) {
      setCaptchaError('Please complete the reCAPTCHA verification.');
      return;
    }
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    const loginPayload = {
      ...formData,
      recaptcha_token: recaptchaToken,
    };

    try {
      const response = await dispatch(loginDoctor(loginPayload));
      
      // Debug logging
      if (process.env.NODE_ENV === 'development') {
        console.log('Login Response:', response);
      }

      // Check if response exists and has the expected structure
      if (response && response.data) {
        const responseData = response.data;
        
        // Debug logging
        if (process.env.NODE_ENV === 'development') {
          console.log('Response Data:', responseData);
        }

        // Since we don't see is_verified in the response, we'll assume the user is verified
        // if they successfully logged in
        toast.success('Welcome back! Redirecting to dashboard...');
        navigate('/app/doctor/dashboard');
        
        // Reset captcha state
        recaptchaRef.current?.reset();
        setRecaptchaToken(null);
        setIsCaptchaVerified(false);
        setSuccessMessage(null);
      } else {
        console.error("Login response structure:", response);
        toast.error('Login completed, but encountered an issue processing the response.');
      }
    } catch (error) {
      console.error("Login error details:", error);
      
      const errorMessage = 
        error?.message ||
        (typeof error === 'string' ? error : null) ||
        error?.response?.data?.non_field_errors?.[0] ||
        error?.response?.data?.detail ||
        error?.response?.data?.recaptcha?.[0] ||
        'Login failed. Please check credentials or try again.';

      toast.error(errorMessage);
      setSnackbar({ open: true, message: errorMessage, severity: 'error' });

      // Reset captcha state
      recaptchaRef.current?.reset();
      setRecaptchaToken(null);
      setIsCaptchaVerified(false);
      setSuccessMessage(null);
    } finally {
      setIsSubmitting(false);
    }
  }, [formData, recaptchaToken, validateForm, dispatch, navigate]);

  // Handle resend verification email
  const handleResendVerification = useCallback(async () => {
    try {
      const response = await dispatch(doctorResendVerificationEmail(formData.email));
      if (response.payload) {
        setSnackbar({
          open: true,
          message: 'Verification email sent! Please check your inbox.',
          severity: 'success',
        });
        setShowVerificationDialog(false);
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Failed to resend verification email.',
        severity: 'error',
      });
    }
  }, [dispatch, formData.email]);

  // Handle password reset request
  const handleRequestReset = useCallback(async () => {
    const { email } = formData;
    if (!email) {
      setSnackbar({
        open: true,
        message: 'Please enter your email.',
        severity: 'error',
      });
      return;
    }
    
    try {
      const response = await dispatch(doctorRequestPasswordReset(email));
      if (response.payload) {
        if (response.payload.isVerified === false) {
          dispatch(openModal());
          setShowVerificationDialog(true);
        } else {
          setSnackbar({
            open: true,
            message: 'Password reset link sent! Please check your email.',
            severity: 'success',
          });
          setShowResetDialog(false);
        }
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Failed to send password reset link.',
        severity: 'error',
      });
    }
  }, [dispatch, formData.email]);

  // Handle Google login
  const handleGoogleLogin = useCallback((credentialResponse) => {
    if (!credentialResponse?.credential) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Google login failed: No credential received');
      }
      return;
    }
    
    dispatch(doctorGoogleLogin(credentialResponse.credential));
  }, [dispatch]);

  // Close snackbar
  const handleCloseSnackbar = useCallback(() => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  }, []);

  return (
    <Box
      sx={{
        display: 'flex',
        height: '100vh',
        width: '100%',
        bgcolor: shades.neutral[100],
        pl: { md: '5%' },
        pr: { md: '5%' },
        pt: { md: '2.5%' },
        pb: { md: '2.5%' },
      }}
    >
      {/* Login Form Section */}
      <Box
        sx={{
          boxShadow: '0 8px 32px rgba(31, 38, 135, 0.37)',
          width: { xs: '100%', md: '50%' },
          overflow: 'auto',
          '&::-webkit-scrollbar': { width: '4px' },
          '&::-webkit-scrollbar-track': { background: 'transparent' },
          '&::-webkit-scrollbar-thumb': {
            background: '#888',
            borderRadius: '4px',
          },
          '&::-webkit-scrollbar-thumb:hover': { background: '#555' },
          display: 'flex',
          alignItems: 'center',
          flexDirection: 'column',
          borderRadius: '8px',
        }}
      >
        <Typography
          variant="h3"
          fontWeight="bold"
          gutterBottom
          align="center"
          sx={{
            mt: { xs: 8, md: 2 },
            mb: { xs: 8, md: 4 },
            color: shades.neutral[900],
          }}
        >
          LOGIN
        </Typography>
        
        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{ backgroundColor: 'white', width: '100%', p: { xs: 2, md: 6 } }}
        >
          <Grid container spacing={2}>
            {/* Email Field */}
            <Grid item xs={12}>
              <TextField
                label="Email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                fullWidth
                required
                error={!!errors.email}
                helperText={errors.email}
                sx={StyledTextField}
              />
            </Grid>
            
            {/* Password Field */}
            <Grid item xs={12}>
              <TextField
                label="Password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={handleChange}
                fullWidth
                required
                error={!!errors.password}
                helperText={errors.password}
                sx={StyledTextField}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword((prev) => !prev)}
                        edge="end"
                        aria-label={showPassword ? 'hide password' : 'show password'}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                <Button
                  onClick={() => setShowResetDialog(true)}
                  sx={{
                    color: shades.neutral[500],
                    textTransform: 'none',
                    '&:hover': {
                      background: 'transparent',
                      color: shades.neutral[700],
                      textDecoration: 'underline',
                    },
                  }}
                >
                  Forgot Password?
                </Button>
              </Box>
            </Grid>

            {/* Captcha Section */}
            {recaptchaSiteKey ? (
              <Grid item xs={12}>
                <CaptchaSection
                  loading={false}
                  error={captchaError}
                  onRecaptchaChange={handleRecaptchaChange}
                  successMessage={successMessage}
                  isCaptchaVerified={isCaptchaVerified}
                  sitekey={recaptchaSiteKey}
                  recaptchaRef={recaptchaRef}
                />
              </Grid>
            ) : (
              <Grid item xs={12}>
                <Typography color="error">reCAPTCHA Site Key not configured.</Typography>
              </Grid>
            )}

            {/* Login Button */}
            <Grid item xs={12}>
              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={isSubmitting}
                sx={StyledButton}
              >
                {isSubmitting ? <CircularProgress size={24} /> : 'Login'}
              </Button>
            </Grid>
          </Grid>

          <Divider sx={{ my: 3, color: shades.neutral[900] }}>Or</Divider>

          {/* Google Login */}
          <GoogleOAuthProvider clientId={clientId}>
            <GoogleLogin
              onSuccess={handleGoogleLogin}
              onError={(error) => {
                if (process.env.NODE_ENV === 'development') {
                  console.error('Google Login Error:', error);
                }
                toast.error('Google login failed. Please try again.');
              }}
              useOneTap
              shape="rectangular"
              text="continue_with"
              context="signin"
            />
          </GoogleOAuthProvider>
        </Box>

        {/* Password Reset Dialog */}
        <Dialog
          open={showResetDialog}
          onClose={() => setShowResetDialog(false)}
          PaperProps={{
            sx: {
              borderRadius: '8px',
              width: '100%',
              maxWidth: '400px',
              bgcolor: 'white',
            },
          }}
        >
          <Box p={3}>
            <Typography
              variant="h6"
              sx={{ mb: 2, color: shades.neutral[900], fontWeight: 'bold' }}
            >
              Reset Password
            </Typography>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleRequestReset();
              }}
            >
              <TextField
                label="Email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                fullWidth
                sx={{ ...StyledTextField, mb: 3 }}
                required
              />
              <Button
                variant="contained"
                type="submit"
                fullWidth
                disabled={loading}
                sx={StyledButton}
              >
                {loading ? <CircularProgress size={24} /> : 'Send Reset Link'}
              </Button>
            </form>
          </Box>
        </Dialog>

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>

      {/* Image Section */}
      <Box
        sx={{
          boxShadow: '0 8px 32px rgba(31, 38, 135, 0.37)',
          overflow: 'hidden',
          display: { xs: 'none', md: 'block' },
          width: '50%',
          bgcolor: 'white',
        }}
      >
        <img
          src={image1}
          style={{ 
            height: '100%', 
            width: '100%', 
            objectFit: 'cover' 
          }}
          alt="Doctor login"
        />
      </Box>
    </Box>
  );
};

export default DoctorLogin;